import React, { useCallback, useEffect, useState } from "react";
import { safeUrl } from "../../utils/safeUrl";
import toast from "react-hot-toast";
import { FiEdit2, FiX, FiSave, FiTrash2, FiPlus, FiExternalLink, FiCheck, FiSliders, FiLink, FiRotateCcw } from "react-icons/fi";
import { usePermission } from "../../context/PermissionContext";
import AdsSectionCard, { Switch, Spinner } from "./AdsSectionCard";
import GoogleAdsNetworks from "./GoogleAdsNetworks";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

/** `ads_setting.adsType` — the number the app reads as `adsSetting.adsType`. */
export const ADS_TYPE_OPTIONS = [
  { value: 1, label: "Facebook Only", hint: "Only Facebook ads are shown." },
  { value: 2, label: "Google Only", hint: "Only Google ads, with the active Google Ads network's IDs." },
  { value: 3, label: "Fallback", hint: "If one ad network has no ad, the app falls back to another." },
  { value: 4, label: "Ads Link Only", hint: "Only the active ads link (below) is shown." },
];

export const adsTypeLabel = (value) =>
  ADS_TYPE_OPTIONS.find((o) => o.value === Number(value))?.label || "Unknown";

/** `/setting/...` call. The setting routes answer failures with HTTP 200 and `success: false`. */
const request = async (method, path, body) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseUrl}/setting${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false || data.status === false) {
    const err = new Error(data?.message || `Request failed with status ${res.status}`);
    err.notFound = /not found/i.test(data?.message || "");
    throw err;
  }
  return data;
};

/* ─── Summary strip ─── */
const SummaryTile = ({ label, value, tone = "neutral" }) => {
  const dot = { good: "bg-green-500", off: "bg-gray-300", warn: "bg-amber-400", neutral: "bg-blue-500" }[tone];
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3 min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-sm font-semibold text-gray-800 mt-1 flex items-center gap-2 min-w-0">
        <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
        <span className="truncate">{value}</span>
      </p>
    </div>
  );
};

/* ─── Ads on/off + ad type ─── */
const AdsGeneralSection = ({ canCreate, canEdit, onSaved }) => {
  const [saved, setSaved] = useState(null); // the ads_setting row, or null when there is none yet
  const [form, setForm] = useState({ isAdsRun: true, adsType: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = useCallback(
    (row) => {
      const clean = row ? { ...row, isAdsRun: !!row.isAdsRun, adsType: Number(row.adsType) || 1 } : null;
      setSaved(clean);
      if (clean) setForm({ isAdsRun: clean.isAdsRun, adsType: clean.adsType });
      onSaved(clean);
    },
    [onSaved]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await request("GET", "/ads-setting");
      apply(Array.isArray(result.data) ? result.data[0] : result.data);
      setError("");
    } catch (err) {
      if (err.notFound) apply(null);
      else setError(err.message || "Failed to fetch ads settings");
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { load(); }, [load]);

  const dirty = !saved || form.isAdsRun !== saved.isAdsRun || form.adsType !== saved.adsType;
  const canSave = saved ? canEdit : canCreate;

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = { isAdsRun: form.isAdsRun, adsType: form.adsType };
      if (saved) {
        await request("PUT", `/ads-setting/${saved.id}`, body);
        apply({ ...saved, ...body });
      } else {
        await request("POST", "/ads-setting", body);
        await load();
      }
      toast.success("Ads settings saved");
    } catch (err) {
      toast.error(err.message || "Failed to save ads settings");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => saved && setForm({ isAdsRun: saved.isAdsRun, adsType: saved.adsType });

  return (
    <AdsSectionCard
      icon={FiSliders}
      title="Ads in the app"
      description="Turn ads on or off for every user, and choose which ads the app shows."
    >
      {loading ? (
        <Spinner />
      ) : error ? (
        <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
      ) : (
        <>
          <div className="p-5 space-y-6">
            {!saved && (
              <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-lg px-4 py-3 text-xs">
                Ads have not been set up yet. Choose the settings below and save to create them.
              </div>
            )}

            <div
              className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3.5 ${
                form.isAdsRun ? "border-green-100 bg-green-50/60" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div>
                <p className="text-sm font-medium text-gray-800">Show ads</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {form.isAdsRun ? "Ads are on for every user." : "Ads are off — no user sees ads."}
                </p>
              </div>
              <Switch
                checked={form.isAdsRun}
                label="Show ads"
                disabled={!canSave}
                onChange={() => setForm((f) => ({ ...f, isAdsRun: !f.isAdsRun }))}
              />
            </div>

            <div>
              <p id="ads-type-label" className="text-xs font-semibold text-gray-600 mb-2">Ad type</p>
              <div role="radiogroup" aria-labelledby="ads-type-label" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {ADS_TYPE_OPTIONS.map((opt) => {
                  const selected = form.adsType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={!canSave}
                      onClick={() => setForm((f) => ({ ...f, adsType: opt.value }))}
                      className={`text-left rounded-xl border px-4 py-3 transition disabled:cursor-not-allowed ${
                        selected
                          ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50 disabled:hover:bg-transparent"
                      }`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-medium ${selected ? "text-blue-700" : "text-gray-800"}`}>{opt.label}</span>
                        {selected && <FiCheck size={15} className="text-blue-600 shrink-0" />}
                      </span>
                      <span className="block text-xs text-gray-500 mt-1">{opt.hint}</span>
                    </button>
                  );
                })}
              </div>
              {!form.isAdsRun && (
                <p className="text-xs text-amber-600 mt-2">Ads are off, so the ad type takes effect only once they are switched back on.</p>
              )}
            </div>
          </div>

          {dirty && canSave && (
            <footer className="px-5 py-3 border-t border-amber-100 bg-amber-50/70 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-medium text-amber-700">
                {saved ? "You have unsaved changes." : "Not saved yet."}
              </span>
              <div className="flex gap-2">
                {saved && (
                  <button
                    type="button"
                    onClick={discard}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition"
                  >
                    <FiRotateCcw size={13} /> Discard
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                >
                  <FiSave size={14} /> {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </footer>
          )}
        </>
      )}
    </AdsSectionCard>
  );
};

/* ─── Ads links ─── */
const AdsLinkSection = ({ canCreate, canEdit, canDelete, onActiveChange }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLink, setNewLink] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const result = await request("GET", "/ads-link");
      setLinks(result.data || []);
      setLoaded(true);
    } catch (err) {
      toast.error(err.message || "Failed to fetch ads links");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  useEffect(() => {
    if (loaded) onActiveChange(links.find((l) => l.isActive) || null);
  }, [links, loaded, onActiveChange]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newLink.trim()) return;
    setCreating(true);
    try {
      await request("POST", "/ads-link", { adsLink: newLink.trim() });
      toast.success("Ads link added");
      setNewLink("");
      fetchLinks();
    } catch (err) {
      toast.error(err.message || "Failed to add the link");
    } finally {
      setCreating(false);
    }
  };

  const closeEditLink = () => {
    setEditingLink(null);
    setEditUrl("");
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editUrl.trim()) return;
    setSavingEdit(true);
    try {
      await request("PUT", `/ads-link/${editingLink.id}`, { adsLink: editUrl.trim() });
      toast.success("Ads link updated");
      setLinks((prev) => prev.map((l) => (l.id === editingLink.id ? { ...l, adsLink: editUrl.trim() } : l)));
      closeEditLink();
    } catch (err) {
      toast.error(err.message || "Failed to update the link");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggle = async (link) => {
    setTogglingId(link.id);
    try {
      await request("PATCH", `/ads-link/${link.id}/toggle`);
      toast.success(link.isActive ? "Link switched off" : "Link is now the active one");
      // Switching one on switches the others off.
      setLinks((prev) =>
        prev.map((l) => (l.id === link.id ? { ...l, isActive: !link.isActive } : link.isActive ? l : { ...l, isActive: false }))
      );
    } catch (err) {
      toast.error(err.message || "Failed to switch the link");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Delete this ads link?\n\n${link.adsLink}`)) return;
    setDeletingId(link.id);
    try {
      await request("DELETE", `/ads-link/${link.id}`);
      toast.success("Ads link deleted");
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
    } catch (err) {
      toast.error(err.message || "Failed to delete the link");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AdsSectionCard
      icon={FiLink}
      iconClass="bg-purple-50 text-purple-600"
      title="Ads links"
      description="The link the app opens for ads (adsSetting.adsLink), used by the Ads Link Only ad type. Only one link is active at a time."
    >
      {canCreate && (
        <form onSubmit={handleCreate} className="px-5 py-4 border-b border-gray-100">
          <label htmlFor="new-ads-link" className="block text-xs font-medium text-gray-500 mb-1">Add a link</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              id="new-ads-link"
              type="url"
              placeholder="https://example.com/ad"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              required
              className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
            <button
              type="submit"
              disabled={creating}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
            >
              <FiPlus size={14} />
              {creating ? "Adding..." : "Add Link"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <Spinner />
      ) : links.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">No ads links yet.</div>
      ) : (
        <ul className="divide-y divide-gray-50">
          {links.map((link) => (
            <li key={link.id} className={`flex flex-wrap items-center gap-3 px-5 py-3.5 ${link.isActive ? "bg-green-50/40" : ""}`}>
              <div className="flex-1 min-w-0">
                <a
                  href={safeUrl(link.adsLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1.5 min-w-0"
                >
                  <FiExternalLink size={12} className="shrink-0" />
                  <span className="truncate">{link.adsLink}</span>
                </a>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Switch
                  checked={link.isActive}
                  label={`${link.adsLink} active`}
                  disabled={!canEdit || togglingId === link.id}
                  onChange={() => handleToggle(link)}
                />
                <span className={`text-xs font-medium w-14 ${link.isActive ? "text-green-600" : "text-gray-400"}`}>
                  {link.isActive ? "Active" : "Inactive"}
                </span>
                {canEdit && (
                  <button
                    onClick={() => { setEditingLink(link); setEditUrl(link.adsLink); }}
                    className="flex items-center gap-1 text-xs text-amber-600 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition"
                  >
                    <FiEdit2 size={13} /> Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => handleDelete(link)}
                    disabled={deletingId === link.id}
                    className="flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition disabled:opacity-60"
                  >
                    <FiTrash2 size={13} /> Delete
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editingLink && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeEditLink}>
          <form onSubmit={handleEditSave} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-gray-800">Edit ads link</h3>
              <button type="button" onClick={closeEditLink} aria-label="Close" className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
            </div>
            <label htmlFor="edit-ads-link" className="block text-xs font-medium text-gray-500 mb-1">Link URL</label>
            <input
              id="edit-ads-link"
              type="url"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              required
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              placeholder="https://example.com/ad"
            />
            <div className="flex gap-3 mt-6">
              <button
                type="submit"
                disabled={savingEdit}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-lg transition"
              >
                <FiSave size={14} />
                {savingEdit ? "Saving..." : "Save Changes"}
              </button>
              <button type="button" onClick={closeEditLink} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdsSectionCard>
  );
};

/* ─── Main Export ─── */
const AdsSettings = () => {
  const { hasPermission } = usePermission();
  const perms = {
    canCreate: hasPermission("setting", "create"),
    canEdit: hasPermission("setting", "edit"),
    canDelete: hasPermission("setting", "delete"),
  };

  // What the summary strip shows; `undefined` until each section has loaded.
  const [setting, setSetting] = useState(undefined);
  const [network, setNetwork] = useState(undefined);
  const [link, setLink] = useState(undefined);

  const onNetworkChange = useCallback((active) => setNetwork(active), []);
  const onLinkChange = useCallback((active) => setLink(active), []);
  const onSettingSaved = useCallback((row) => setSetting(row), []);

  const loadingText = "…";
  const adsOn = setting ? setting.isAdsRun : false;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ads-summary">
        <SummaryTile
          label="Ads"
          value={setting === undefined ? loadingText : setting === null ? "Not set up" : adsOn ? "On" : "Off"}
          tone={setting ? (adsOn ? "good" : "off") : "warn"}
        />
        <SummaryTile
          label="Ad type"
          value={setting === undefined ? loadingText : setting === null ? "—" : adsTypeLabel(setting.adsType)}
        />
        <SummaryTile
          label="Google network"
          value={network === undefined ? loadingText : network ? network.name : "None active"}
          tone={network ? "good" : "warn"}
        />
        <SummaryTile
          label="Ads link"
          value={link === undefined ? loadingText : link ? "1 active" : "None active"}
          tone={link ? "good" : "off"}
        />
      </div>

      <AdsGeneralSection {...perms} onSaved={onSettingSaved} />
      <GoogleAdsNetworks onActiveChange={onNetworkChange} />
      <AdsLinkSection {...perms} onActiveChange={onLinkChange} />
    </div>
  );
};

export default AdsSettings;
